import {
  AllergicSubstance,
  Patient,
  PatientIdentityChangeOutcome,
  PatientSearchResult,
  Priority,
  Surgery,
  SurgicalEvent
} from '@/modules/surgicalEvent/model';
import {
  IdentityChangePreviewPayload,
  SearchPatientPayload,
  SurgicalEventService
} from '@/modules/surgicalEvent/surgicalEvent.service';
import { ID } from '@ianesthesie-kit/services';
import { minutesToSeconds } from 'date-fns/fp';
import { AnesthesiaType } from '@/modules/surgicalEvent/anesthesia';
import { wrapError } from './util';
import { doctorBffApi, invalidatePatientScopedTags } from '@/modules';
import { MedicalDocument } from '@/modules/surgicalEvent/medicalDocument';
import {
  FetchTemplatePayload,
  GenerateTemplatePayload,
  TemplateEditorData,
  UpsertTemplatePayload
} from '@/modules/surgicalEvent/model/medicalTemplate.model';

export type PatientId = { patientId: ID };
export type WithPatientId<T, K extends string = 'value'> = PatientId &
  (T extends object ? T : { [a in K]: T });

export const surgicalEventApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    surgeryGlobalList: builder.query<Surgery[], void>({
      keepUnusedDataFor: minutesToSeconds(10),
      queryFn: () =>
        SurgicalEventService.getInstance()
          .fetchSurgeryGlobalList()
          .then(({ data }) => ({ data }))
          .catch(wrapError)
    }),

    anesthesiaTypeGlobalList: builder.query<AnesthesiaType[], void>({
      keepUnusedDataFor: minutesToSeconds(10),
      queryFn: () =>
        SurgicalEventService.getInstance()
          .fetchAnesthesiaTypeGlobalList()
          .then(({ data }) => ({ data }))
          .catch(wrapError)
    }),

    allergicSubstanceGlobalList: builder.query<AllergicSubstance[], void>({
      keepUnusedDataFor: minutesToSeconds(10),
      queryFn: () =>
        SurgicalEventService.getInstance()
          .fetchAllergicSubstanceGlobalList()
          .then(({ data }) => ({ data }))
          .catch(wrapError)
    }),

    surgeonNameGlobalList: builder.query<string[], void>({
      keepUnusedDataFor: minutesToSeconds(10),
      providesTags: ['SurgeonName'],
      queryFn: () =>
        SurgicalEventService.getInstance()
          .fetchSurgeonNamesGlobalList()
          .then(({ data }) => ({ data }))
          .catch(wrapError)
    }),

    deleteSurgeonNameFromGlobalList: builder.mutation<void, { name: string }>({
      invalidatesTags: ['SurgeonName'],
      queryFn: ({ name }) =>
        SurgicalEventService.getInstance()
          .deleteSurgeonNameFromGlobalList(name)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError)
    }),

    activeSurgicalEvent: builder.query<SurgicalEvent, PatientId>({
      providesTags: (result, error, { patientId }) => [
        { type: 'ActiveSurgicalEvent', id: patientId }
      ],
      queryFn: ({ patientId }) =>
        SurgicalEventService.getInstance()
          .fetchActive(patientId)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError)
    }),

    updateSurgicalEventValidationFields: builder.mutation<
      SurgicalEvent,
      PatientId
    >({
      queryFn: ({ patientId }) =>
        SurgicalEventService.getInstance()
          .updateValidationFields(patientId)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError),

      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedSurgicalEvent } = await queryFulfilled;
          dispatch(
            surgicalEventApi.util.updateQueryData(
              'activeSurgicalEvent',
              { patientId },
              (draft) => {
                Object.assign(draft, updatedSurgicalEvent);
              }
            )
          );
        } catch {
          dispatch(
            surgicalEventApi.util.invalidateTags([
              { type: 'ActiveSurgicalEvent', id: patientId }
            ])
          );
        }
      }
    }),

    updateSurgicalEventPriority: builder.mutation<
      SurgicalEvent,
      WithPatientId<{ priority: Priority }>
    >({
      invalidatesTags: ['Dashboard'],
      queryFn: ({ priority, patientId }) =>
        SurgicalEventService.getInstance()
          .updatePriority(priority, patientId)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError),

      async onQueryStarted(
        { patientId, priority },
        { dispatch, queryFulfilled }
      ) {
        dispatch(
          surgicalEventApi.util.updateQueryData(
            'activeSurgicalEvent',
            { patientId },
            (draft) => {
              draft.priority = priority;
            }
          )
        );
        try {
          const { data: updatedSurgicalEvent } = await queryFulfilled;
          dispatch(
            surgicalEventApi.util.updateQueryData(
              'activeSurgicalEvent',
              { patientId },
              (draft) => {
                Object.assign(draft, updatedSurgicalEvent);
              }
            )
          );
        } catch {
          dispatch(
            surgicalEventApi.util.invalidateTags([
              { type: 'ActiveSurgicalEvent', id: patientId }
            ])
          );
        }
      }
    }),

    updateSurgicalEventStaffNotes: builder.mutation<
      SurgicalEvent,
      WithPatientId<{ content: string }>
    >({
      queryFn: ({ content, patientId }) =>
        SurgicalEventService.getInstance()
          .updateStaffComents(content, patientId)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError),
      invalidatesTags: (result) => {
        if (result) {
          return [{ type: 'Dashboard', id: 'WITH_STAFF_NOTES' }];
        }
        return [];
      },
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedSurgicalEvent } = await queryFulfilled;
          dispatch(
            surgicalEventApi.util.updateQueryData(
              'activeSurgicalEvent',
              { patientId },
              (draft) => {
                Object.assign(draft, updatedSurgicalEvent);
              }
            )
          );
        } catch {
          dispatch(
            surgicalEventApi.util.invalidateTags([
              { type: 'ActiveSurgicalEvent', id: patientId }
            ])
          );
        }
      }
    }),

    updateSurgicalEventContextNotes: builder.mutation<
      SurgicalEvent,
      WithPatientId<{ content: string }>
    >({
      queryFn: ({ content, patientId }) =>
        SurgicalEventService.getInstance()
          .updateContextNotes(content, patientId)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError),
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedSurgicalEvent } = await queryFulfilled;
          dispatch(
            surgicalEventApi.util.updateQueryData(
              'activeSurgicalEvent',
              { patientId },
              (draft) => {
                Object.assign(draft, updatedSurgicalEvent);
              }
            )
          );
        } catch {
          dispatch(
            surgicalEventApi.util.invalidateTags([
              { type: 'ActiveSurgicalEvent', id: patientId }
            ])
          );
        }
      }
    }),

    createFollowUpEvent: builder.mutation<
      SurgicalEvent,
      Parameters<SurgicalEventService['createFollowUp']>[0]
    >({
      onQueryStarted: async (arg, api) => {
        const result = (await api.queryFulfilled).data;
        if (!result) {
          return;
        }

        invalidatePatientScopedTags(api, result.patient.id, result.id);
      },
      queryFn: (payload) =>
        SurgicalEventService.getInstance()
          .createFollowUp(payload)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError)
    }),

    patchPatient: builder.mutation<Patient, Partial<Patient> & { id: ID }>({
      invalidatesTags: (result, error, { id: patientId }) => [
        { type: 'ActiveSurgicalEvent', id: patientId },
        'Dashboard',
        'SearchSurgicalEvents',
        'Scores',
        'MedicalHo',
        'Allergy',
        'Meds',
        'LabTest',
        'MedicalDocument',
        'PregnancyInfo',
        'SurgeryInfo',
        'ProtocolStatuses',
        'Questionnaires'
      ],
      queryFn: (patient) =>
        SurgicalEventService.getInstance()
          .patchPatient(patient)
          .then((r) => ({
            data: r.data
          }))
          .catch(wrapError)
    }),

    previewIdentityChange: builder.mutation<
      PatientIdentityChangeOutcome,
      IdentityChangePreviewPayload
    >({
      queryFn: (payload) =>
        SurgicalEventService.getInstance()
          .previewIdentityChange(payload)
          .then((r) => ({ data: r.data }))
          .catch(wrapError)
    }),

    searchSurgicalEvents: builder.query<
      PatientSearchResult[],
      SearchPatientPayload
    >({
      providesTags: ['SearchSurgicalEvents'],
      queryFn: (payload) =>
        SurgicalEventService.getInstance()
          .searchPatients(payload)
          .then((r) => ({ data: r.data }))
          .catch((err) => {
            //TODO handle error, if status >= 500
            console.log(err);
            return {
              data: []
            };
          })
    }),

    generateFullReport: builder.mutation<MedicalDocument, PatientId>({
      queryFn: ({ patientId }) =>
        SurgicalEventService.getInstance()
          .generateCompleteReport(patientId)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        !error ? [{ type: 'MedicalDocument', id: `${patientId}_byType` }] : []
    }),

    fetchTemplateEditorData: builder.query<
      TemplateEditorData[],
      FetchTemplatePayload
    >({
      keepUnusedDataFor: 10,
      queryFn: (payload) =>
        SurgicalEventService.getInstance()
          .fetchTemplateData(payload)
          .catch(wrapError)
    }),
    upsertTemplateEditorData: builder.mutation<
      TemplateEditorData,
      UpsertTemplatePayload
    >({
      queryFn: (payload) =>
        SurgicalEventService.getInstance()
          .upsertTemplateData(payload)
          .catch(wrapError)
    }),
    generateTemplate: builder.mutation<
      MedicalDocument,
      GenerateTemplatePayload
    >({
      invalidatesTags: (result, error, arg) => [
        { type: 'MedicalDocument', id: arg.patientId }
      ],
      queryFn: (payload) =>
        SurgicalEventService.getInstance()
          .generateTemplate(payload)
          .catch(wrapError)
    })
  })
});

export const {
  useActiveSurgicalEventQuery,
  useCreateFollowUpEventMutation,
  useUpdateSurgicalEventValidationFieldsMutation,
  useUpdateSurgicalEventPriorityMutation,
  useUpdateSurgicalEventStaffNotesMutation,
  useUpdateSurgicalEventContextNotesMutation,
  useSurgeryGlobalListQuery: useSurgeryGlobalList,
  useAnesthesiaTypeGlobalListQuery: useAnesthesiaTypeGlobalList,
  useAllergicSubstanceGlobalListQuery: useAllergicSubstanceGlobalList,
  useSurgeonNameGlobalListQuery: useSurgeonNameGlobalList,
  useDeleteSurgeonNameFromGlobalListMutation,
  usePatchPatientMutation,
  usePreviewIdentityChangeMutation,
  useSearchSurgicalEventsQuery: useSearchSurgicalEvents,
  useGenerateFullReportMutation: useGenerateFullReport,
  useFetchTemplateEditorDataQuery: useTemplateEditorData,
  useUpsertTemplateEditorDataMutation: useUpsertTemplateEditorData,
  useGenerateTemplateMutation: useGenerateTemplate
} = surgicalEventApi;
